import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';

import { dashboardApi } from '../../services/api';
import type {
    HomeworkAlert,
    HomeworkAlertFilters,
    HomeworkAlertListResponse,
    HomeworkAlertStaffMember,
    HomeworkAlertStatus,
} from '../../types';
import {
    DataToolbar,
    EmptyState,
    ErrorState,
    Input,
    LocalPagination,
    LoadingState,
    ResponsiveDataView,
    Select,
    StatusBadge,
    SearchSelect,
 } from '../ui';
import { classOptionsByCampus } from '../../utils/pickerOptions';
import { openOnRowClick } from '../../utils/rowClick';
import HomeworkAlertDrawer from './HomeworkAlertDrawer';
import { ALERT_PAGE_SIZE_OPTIONS, usePagedAlerts } from './usePagedAlerts';

interface HomeworkAlertBoardProps {
    refreshToken: number;
    // Open + in progress, for the dashboard tab.
    onCount?: (count: number) => void;
}

const statusLabels: Record<HomeworkAlertStatus, string> = {
    OPEN: 'Cần xử lý',
    IN_PROGRESS: 'Đang xử lý',
    RESOLVED: 'Hoàn tất',
};

const statusTones: Record<HomeworkAlertStatus, 'danger' | 'warning' | 'success'> = {
    OPEN: 'danger',
    IN_PROGRESS: 'warning',
    RESOLVED: 'success',
};

function studentName(alert: HomeworkAlert) {
    return `${alert.student.firstName} ${alert.student.lastName}`.trim();
}

function formatShortDate(value: string) {
    const date = new Date(value);
    return [
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
        date.getFullYear(),
    ].join('/');
}

function fullName(person: Pick<HomeworkAlertStaffMember, 'firstName' | 'lastName'>) {
    return `${person.firstName} ${person.lastName}`.trim();
}

function renderStaffMembers(
    members: HomeworkAlertStaffMember[],
    emptyLabel: string,
) {
    if (!members.length) {
        return <span className="text-ink-400">{emptyLabel}</span>;
    }

    return (
        <div className="space-y-1">
            {members.map((member) => (
                <div key={member.id}>
                    <p className="font-medium text-ink-700">{fullName(member)}</p>
                    {member.phone ? (
                        <a className="text-xs text-academy-700 hover:text-academy-800" href={`tel:${member.phone}`}>
                            {member.phone}
                        </a>
                    ) : (
                        <p className="text-xs text-ink-400">Chưa có SĐT</p>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function HomeworkAlertBoard({ refreshToken, onCount }: HomeworkAlertBoardProps) {
    const [searchParams] = useSearchParams();
    const [selectedAlertId, setSelectedAlertId] = React.useState<string | null>(null);
    const fetchPage = React.useCallback(
        async (filters: HomeworkAlertFilters) => (await dashboardApi.getHomeworkAlerts(filters)).data.data as HomeworkAlertListResponse,
        [],
    );
    const { data, loading, error, filters, setFilter, patchFilters, setPage, searchInput, setSearchInput, reload } = usePagedAlerts(fetchPage, refreshToken);

    React.useEffect(() => {
        if (data && onCount) onCount(data.counts.OPEN + data.counts.IN_PROGRESS);
    }, [data, onCount]);

    React.useEffect(() => {
        const alertId = searchParams.get('homeworkAlertId');
        if (alertId) setSelectedAlertId(alertId);
    }, [searchParams]);

    const renderStatus = (status: HomeworkAlertStatus) => (
        <StatusBadge tone={statusTones[status]}>{statusLabels[status]}</StatusBadge>
    );

    const renderLessons = (alert: HomeworkAlert) => (
        <div className="flex flex-wrap gap-2 text-xs">
            {[alert.firstLesson, alert.secondLesson].map((lesson) => (
                <span key={lesson.attendanceId} className="rounded-lg border border-danger-100 bg-danger-50 px-2 py-1 font-medium text-danger-700">
                    <span>{formatShortDate(lesson.lessonDate)}</span>
                    <span aria-hidden="true" className="mx-1">·</span>
                    <span>{Math.round(lesson.completionPercent)}%</span>
                </span>
            ))}
        </div>
    );

    const open = (alert: HomeworkAlert) => openOnRowClick(() => setSelectedAlertId(alert.id));
    const items = data?.items ?? [];
    const header = 'px-4 py-3 text-left text-xs font-medium text-ink-500';

    return (
        <div role="region" aria-label="Bảng cảnh báo bài tập" className="space-y-4">
            <p className="text-sm text-ink-500">Học sinh hoàn thành dưới 30% bài tập trong hai buổi học hợp lệ gần nhất.</p>
            <div role="group" aria-label="Bộ lọc bài tập về nhà">
                <DataToolbar className="shadow-none sm:items-center" controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
                    <Input
                        aria-label="Tìm kiếm học sinh"
                        className="min-h-9 lg:w-72"
                        placeholder="Tên học sinh, lớp..."
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        leftIcon={<Search className="h-4 w-4" />}
                    />
                    <Select
                        aria-label="Lọc theo cơ sở"
                        className="min-h-9 lg:w-44"
                        value={filters.campusId ?? ''}
                        onChange={(event) => patchFilters({ campusId: event.target.value || undefined, classId: undefined })}
                    >
                        <option value="">Tất cả cơ sở</option>
                        {data?.filters.campuses.map((campus) => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
                    </Select>
                    <SearchSelect
                        aria-label="Lọc theo lớp học"
                        className="min-h-9 lg:w-44"
                        value={filters.classId ?? ''}
                        onChange={(value) => setFilter('classId', value)}
                        emptyOption="Tất cả lớp"
                        options={classOptionsByCampus(data?.filters.classes ?? [], data?.filters.campuses ?? [], filters.campusId)}
                    />
                    <Select
                        aria-label="Lọc theo trạng thái"
                        className="min-h-9 lg:w-56"
                        value={filters.status ?? ''}
                        onChange={(event) => setFilter('status', event.target.value as HomeworkAlertStatus | '')}
                    >
                        <option value="">Cần xử lý + đang xử lý</option>
                        {(Object.keys(statusLabels) as HomeworkAlertStatus[]).map((status) => (
                            <option key={status} value={status}>{statusLabels[status]} ({data?.counts[status] ?? 0})</option>
                        ))}
                    </Select>
                </DataToolbar>
            </div>

            {error ? <ErrorState title="Không thể tải cảnh báo" description={error} onRetry={() => void reload()} /> : null}
            {!error && loading && !data ? <LoadingState title="Đang tải cảnh báo bài tập..." /> : null}
            {!error && data && !items.length ? (
                <EmptyState
                    title={filters.status ? `Không có cảnh báo ${statusLabels[filters.status].toLowerCase()}` : 'Không có bài tập về nhà cần xử lý'}
                    description="Danh sách sẽ tự cập nhật khi có dữ liệu bài tập mới."
                    className="min-h-36"
                />
            ) : null}
            {!error && items.length ? (
                <div className="overflow-hidden rounded-lg border border-ink-200 max-lg:border-0">
                    <ResponsiveDataView
                        desktopLabel="Danh sách bài tập về nhà"
                        mobileLabel="Danh sách bài tập về nhà (di động)"
                        desktop={(
                            <table className="min-w-full divide-y divide-ink-200 text-sm">
                                <thead className="bg-ink-50">
                                    <tr>
                                        <th className={header}>Học sinh</th>
                                        <th className={header}>Lớp / cơ sở</th>
                                        <th className={header}>Giáo viên</th>
                                        <th className={header}>Trợ giảng</th>
                                        <th className={header}>Hai buổi gần nhất</th>
                                        <th className={header}>Trạng thái</th>
                                        <th className={header}><span className="sr-only">Xem</span></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-ink-100 bg-white">
                                    {items.map((alert) => (
                                        <tr
                                            key={alert.id}
                                            aria-label={`Học sinh ${studentName(alert)}`}
                                            onClick={open(alert)}
                                            className="cursor-pointer align-top hover:bg-ink-100/60"
                                        >
                                            <td className="px-4 py-3">
                                                <p className="font-medium text-ink-900">{studentName(alert)}</p>
                                                {alert.student.parentPhone ? <p className="mt-0.5 text-xs text-ink-500">{alert.student.parentPhone}</p> : null}
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-ink-800">{alert.class.name}</p>
                                                <p className="text-xs text-ink-500">{alert.class.campus.name}</p>
                                            </td>
                                            <td className="px-4 py-3 text-ink-600">{renderStaffMembers(alert.class.teachers, 'Chưa có giáo viên')}</td>
                                            <td className="px-4 py-3 text-ink-600">{renderStaffMembers(alert.class.assistants, 'Chưa có trợ giảng')}</td>
                                            <td className="px-4 py-3">{renderLessons(alert)}</td>
                                            <td className="px-4 py-3">{renderStatus(alert.status)}</td>
                                            <td className="px-2 py-3 text-ink-400"><ChevronRight className="h-4 w-4" /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        mobile={items.map((alert) => (
                            <article key={alert.id} onClick={open(alert)} className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h4 className="font-semibold text-ink-900">{studentName(alert)}</h4>
                                        <p className="mt-1 text-sm text-ink-600">{alert.class.name} · {alert.class.campus.name}</p>
                                        {alert.student.parentPhone ? <p className="mt-1 text-xs text-ink-500">{alert.student.parentPhone}</p> : null}
                                    </div>
                                    {renderStatus(alert.status)}
                                </div>
                                <div className="mt-3">{renderLessons(alert)}</div>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-ink-500">
                                    <div>
                                        <p className="mb-1 font-medium text-ink-600">Giáo viên</p>
                                        {renderStaffMembers(alert.class.teachers, 'Chưa có giáo viên')}
                                    </div>
                                    <div>
                                        <p className="mb-1 font-medium text-ink-600">Trợ giảng</p>
                                        {renderStaffMembers(alert.class.assistants, 'Chưa có trợ giảng')}
                                    </div>
                                </div>
                            </article>
                        ))}
                    />
                </div>
            ) : null}
            {!error && data ? (
                <LocalPagination
                    label="bài tập về nhà"
                    page={data.pagination.page}
                    pageSize={data.pagination.pageSize}
                    totalItems={data.pagination.total}
                    pageSizeOptions={ALERT_PAGE_SIZE_OPTIONS}
                    onPageChange={setPage}
                    onPageSizeChange={(pageSize) => setFilter('pageSize', pageSize)}
                    className="border-t-0 px-0"
                />
            ) : null}

            <HomeworkAlertDrawer
                alertId={selectedAlertId}
                onClose={() => setSelectedAlertId(null)}
                onChanged={reload}
            />
        </div>
    );
}
